import { useState } from 'react';
import { X, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface PasswordVerifyModalProps {
  onConfirm: (password: string) => Promise<boolean>;
  onCancel: () => void;
}

export const PasswordVerifyModal: React.FC<PasswordVerifyModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Veuillez saisir votre mot de passe');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const isValid = await onConfirm(password);
      if (!isValid) {
        setError('Mot de passe incorrect');
        setPassword('');
      }
    } catch {
      setError('Erreur lors de la vérification du mot de passe');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-verification-title"
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
              <Lock className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2
                id="password-verification-title"
                className="text-xl font-semibold text-neutral-900"
              >
                Vérification requise
              </h2>
              <p className="text-sm text-neutral-600">
                Saisissez votre mot de passe pour voir les identifiants
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fermer la vérification"
            className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="password"
              className="mb-2 block text-sm font-medium text-neutral-700"
            >
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Entrez votre mot de passe"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2 text-base transition-colors placeholder:text-neutral-400/50 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              autoFocus
              disabled={isVerifying}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-error-50 p-3 text-sm text-error-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              onClick={onCancel}
              variant="outline"
              disabled={isVerifying}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isVerifying || !password}
            >
              {isVerifying ? 'Vérification...' : 'Confirmer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
